import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useFormik} from 'formik';
import {HospitalEnrichmentData, UpdateHopsitalProfileRequestPaylaod} from '@/interface';
import {HospitalProfileEditValidationSchema} from '@/utils/schema';
import {Button, PhoneInput, SelectInput, Text, TextInput, MultiSelectInput, CurrencyInput, Icon} from '@/ui-kits';
import {HospitalFeesModal} from './HospitalFeesModal';
import {ProfileDetailSections} from './ProfileDetailSections';
import {allAppSpecializations, appCountries, hospitalAdminProfileEditLoadingSelector} from '@/services/redux/selectors';
import {useSelector} from 'react-redux';
import {cn, enumToSelectOptions} from '@/utils/common_functions';
import {HospitalType, onlyAlphaRegex, HospitalTypeLabel, SUCCESS_KEY} from '@/constants';
import {Globe, Loader2, Upload} from 'lucide-react';
import {useDebounceState, useToast} from '@/hooks';
import {ErrorCodes, getErrorMessage} from '@/utils/getMessages';
import {getHospitalDetailsFromEnrichment} from '@/services/api';

const HospitalSelectInputOptions = enumToSelectOptions(HospitalType, HospitalTypeLabel);

interface HopsitalProfileEditFormPropInterface {
  profile: UpdateHopsitalProfileRequestPaylaod;
  onSave: (value: UpdateHopsitalProfileRequestPaylaod) => void;
  lockedSpecializations: number[];
}
export const HospitalProfileEditForm: React.FC<HopsitalProfileEditFormPropInterface> = props => {
  const {profile, onSave, lockedSpecializations} = props;

  // hooks
  const {values, handleChange, handleBlur, errors, touched, setFieldValue, handleSubmit, dirty, isValid} = useFormik<UpdateHopsitalProfileRequestPaylaod>({
    initialValues: profile,
    validationSchema: HospitalProfileEditValidationSchema,
    onSubmit: onSave,
    enableReinitialize: true,
  });

  // Modal state for fee change
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [pendingFee, setPendingFee] = useState<number | null>(null);
  // Track if modal has been shown this session
  const feeModalShown = useRef<boolean>(false);
  // Handler for consultation_fee blur
  const handleConsultationFeeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur('consultation_fee')(e);
    if (feeModalShown.current) return;
    const originalFee = profile.consultation_fee ?? 0;
    const newFee = Number(values.consultation_fee ?? 0);
    if (newFee !== originalFee) {
      setPendingFee(newFee);
      setShowFeeModal(true);
      feeModalShown.current = true;
    }
  };

  const handleFeeModalClose = () => {
    setShowFeeModal(false);
    setPendingFee(null);
    // Optionally reset the fee if not confirmed
    // setFieldValue('consultation_fee', profile.consultation_fee ?? 0);
  };

  const handleFeeModalConfirm = () => {
    setShowFeeModal(false);
    setPendingFee(null);
    // Keep the new fee as is
  };
  const {showToast} = useToast();

  // selectors
  const countryList = useSelector(appCountries);
  const specializationsList = useSelector(allAppSpecializations);
  const profileEditLoading = useSelector(hospitalAdminProfileEditLoadingSelector);

  // states
  const inputRef = useRef<HTMLInputElement | null>(null);
  const countryCode = useMemo(() => {
    return countryList.find(item => item.id === values.country)?.country_code;
  }, [countryList, values.country]);
  const [autoFillWebsite, setAutoFillWebsite, immediateAutoFillWebsite] = useDebounceState<string>('');
  const [autoFillWebsiteError, setAutoFillWebsiteError] = useState<string | null>(null);
  const [autoFillWebsiteLoading, setAutoFillWebsiteLoading] = useState<boolean>(false);
  const showWebsiteError = useMemo(() => {
    return !autoFillWebsiteLoading && autoFillWebsiteError;
  }, [autoFillWebsiteLoading, autoFillWebsiteError]);

  async function handleAutoFillWebsite(url: string) {
    setAutoFillWebsiteLoading(true);
    setAutoFillWebsiteError(null);
    try {
      const response = await getHospitalDetailsFromEnrichment({url});
      if (response.data.status === SUCCESS_KEY) {
        const data: HospitalEnrichmentData = response.data.data;
        if (data.name) {
          setFieldValue('name', data.name);
        }
        if (data.address) {
          setFieldValue('address', data.address);
        }
        if (data.city) {
          setFieldValue('city', data.city);
        }
        if (data.state) {
          setFieldValue('state', data.state);
        }
        if (data.country) {
          setFieldValue('country', data.country);
        }
        if (data.phone) {
          setFieldValue('phone', data.phone);
        }
        if (data.email) {
          setFieldValue('email', data.email);
        }
        if (data.hospital_type) {
          setFieldValue('hospital_type', data.hospital_type);
        }
        if (data.about) {
          setFieldValue('about', data.about);
        }
        if (data.accreditation) {
          setFieldValue('accreditation', data.accreditation);
        }
        if (data.specializations) {
          setFieldValue(
            'specializations',
            data.specializations.map(item => item.id),
          );
        }
      } else {
        let errorCode = response.data.status_code as ErrorCodes;
        if (errorCode === 'E-10001'){
          errorCode = 'E-10215';
        }
        setAutoFillWebsiteError(errorCode);
      }
    } catch (error) {
      setAutoFillWebsiteError(getErrorMessage('E-10215'));
    } finally {
      setAutoFillWebsiteLoading(false);
    }
  }

  // functions
  const handleOpenFilePicker = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // check for size validation
      const imageSize = file.size;
      if (imageSize > 5 * 1024 * 1024) {
        showToast(getErrorMessage('E-10013'), 'failure');
        return;
      }
      setFieldValue('image', file);
    }

    e.target.value = '';
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const imageSource = useMemo(() => {
    if (!values.image) return null;
    if (typeof values.image === 'string') return values.image;
    return URL.createObjectURL(values.image);
  }, [values.image]);

  // ==============================
  // side effect
  // ==============================
  useEffect(() => {
    if (!autoFillWebsite) return;
    handleAutoFillWebsite(autoFillWebsite);
  }, [autoFillWebsite]);

  return (
    <>
      <Text variant="heading" className="text-ui_blue">
        Add Hospital Details
      </Text>

      <div className="flex flex-col gap-1 border border-ui_blue_light p-4 rounded-lg bg-white shadow">
        <div className="flex items-center gap-2">
          <Icon type="ai_icon" />
          <Text variant="title" className="text-ui_blue">
            Auto-fill from Website
          </Text>
        </div>
        <Text variant="body" className="text-ui_input_border_dark">
          Enter a hospital website URL and we'll automatically fetch details like name, address, specializations, and more.
        </Text>
        <div
          className={cn(
            'flex items-center mt-2 gap-2 border p-2 rounded-lg border-ui_input_border',
            showWebsiteError && 'border-rejection',
            autoFillWebsiteLoading && 'border-ui_disabled',
          )}>
          <Globe className="size-4 text-ui_placeholder" />
          <input
            disabled={autoFillWebsiteLoading}
            type="text"
            placeholder="https://www.hospital-website.com"
            className="outline-none grow placeholder:text-ui_placeholder bg-transparent disabled:cursor-not-allowed"
            value={immediateAutoFillWebsite}
            onChange={e => setAutoFillWebsite(e.target.value)}
          />
          {autoFillWebsiteLoading && !showWebsiteError && <Loader2 className="size-4 animate-spin text-ui_blue" />}
        </div>
        {showWebsiteError && (
          <Text variant="body" className="text-rejection">
            {autoFillWebsiteError}
          </Text>
        )}
      </div>

      <ProfileDetailSections
        title="Onboarding Details"
        icon="home_info"
        wrapperClassName="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-8 lg:gap-x-12 gap-y-4">
        <TextInput
          value={values.name}
          onChangeText={handleChange('name')}
          onBlur={handleBlur('name')}
          error={errors.name}
          touched={touched.name}
          placeholder="Enter Hospital Name"
          required
          preventLeadingSpace
          preventTrailingSpace
          max={150}
          label="Hospital Name"
          wrapperClassName="bg-white border"
        />

        {/* image upload section */}
        <div className="md:row-span-2 flex flex-col">
          <Text className="!font-FigtreeSemiBold gap-1 inline-flex text-ui_blue">
            Hospital Image
            <p className="text-rejection">*</p>
          </Text>

          <div
            className={cn(
              'h-[136px] cursor-pointer hover:bg-gray-100 w-[150px] bg-white flex flex-col items-center relative overflow-hidden justify-center border-dashed border-ui_input_border rounded-lg',
              !imageSource && 'border-2',
            )}>
            {imageSource ? (
              <React.Fragment>
                <img src={imageSource} alt="Hospital Image" className="absolute w-full h-full object-cover" />
                <button className="bg-white z-10 border border-ui_blue py-1 rounded-lg mt-auto w-[90%] mb-2" onClick={handleOpenFilePicker}>
                  <Text variant="small" className="gradient-text">
                    Upload New
                  </Text>
                </button>
              </React.Fragment>
            ) : (
              <button className="p-2 w-full h-full flex flex-col items-center justify-center" onClick={handleOpenFilePicker}>
                <Upload className="text-ui_blue_light mb-4" />
                <Text variant="tiny" className="text-center text-ui_blue_light">
                  Click to upload photo JPG or PNG, max 5MB
                </Text>
              </button>
            )}
          </div>

          <input ref={inputRef} onChange={handleFileChange} accept=".jpg,.jpeg,.png" type="file" className="hidden" />
        </div>

        <TextInput
          value={values.email}
          onChangeText={handleChange('email')}
          onBlur={handleBlur('email')}
          error={errors.email}
          touched={touched.email}
          placeholder="Enter public inquiry mail ID"
          required
          preventLeadingSpace
          preventTrailingSpace
          label="Email Address"
          wrapperClassName="bg-white border"
        />
        <PhoneInput
          value={values.phone ?? ''}
          onChange={(phone: string) => setFieldValue('phone', phone)}
          onBlur={handleBlur('phone')}
          error={errors.phone}
          touched={touched.phone}
          placeholder="Ener public desk / reception phone no."
          required
          lockCountry
          country={values.country}
          label="Phone Number"
          wrapperClassName="bg-white border"
        />
        <SelectInput
          value={values.hospital_type}
          options={HospitalSelectInputOptions}
          onChange={value => setFieldValue('hospital_type', value.id)}
          onBlur={handleBlur('hospital_type')}
          error={errors.hospital_type}
          touched={touched.hospital_type}
          placeholder="Select hospital type"
          itemTextClassName="text-ui_blue !font-FigtreeMedium"
          required
          label="Hospital type"
          wrapperClassName="bg-white border"
        />
        <TextInput
          value={values.address}
          onChangeText={handleChange('address')}
          onBlur={handleBlur('address')}
          max={300}
          preventLeadingSpace
          preventTrailingSpace
          error={errors.address}
          touched={touched.address}
          placeholder="Enter address line 1"
          required
          label="Address"
          wrapperClassName="bg-white border"
          className="md:col-span-2"
        />
        <TextInput
          value={values.city}
          allowedRegex={onlyAlphaRegex}
          onChangeText={handleChange('city')}
          onBlur={handleBlur('city')}
          error={errors.city}
          touched={touched.city}
          placeholder="Enter city"
          required
          preventLeadingSpace
          preventTrailingSpace
          label="City"
          wrapperClassName="bg-white border"
        />
        <TextInput
          value={values.state}
          allowedRegex={onlyAlphaRegex}
          onChangeText={handleChange('state')}
          onBlur={handleBlur('state')}
          error={errors.state}
          touched={touched.state}
          placeholder="Enter state"
          required
          preventLeadingSpace
          preventTrailingSpace
          label="State"
          wrapperClassName="bg-white border"
        />
        <SelectInput
          value={values.country}
          options={countryList}
          onChange={value => {
            setFieldValue('fee_currency', value.id);
            setFieldValue('country', value.id);
          }}
          onBlur={handleBlur('country')}
          error={errors.country}
          touched={touched.country}
          placeholder="Select country"
          required
          label="Country"
          wrapperClassName="bg-white border"
        />
        <CurrencyInput
          currency={values.fee_currency}
          value={String(values.consultation_fee ?? '')}
          onChange={amount => setFieldValue('consultation_fee', Number(amount))}
          lockCurrency
          onBlur={handleConsultationFeeBlur}
          error={errors.consultation_fee}
          touched={touched.consultation_fee}
          placeholder="Enter consultation fee"
          required
          label="Consultation Fee"
          wrapperClassName="bg-white border"
        />
        {/* Fee Change Modal */}
        {showFeeModal && (
          <HospitalFeesModal
            open={showFeeModal}
            onClose={handleFeeModalClose}
            originalFee={profile.consultation_fee ?? 0}
            newFee={pendingFee ?? 0}
            onConfirm={handleFeeModalConfirm}
          />
        )}
      </ProfileDetailSections>

      <ProfileDetailSections wrapperClassName="flex flex-col gap-4" title="Hospital Overview" icon="certification">
        <TextInput
          value={String(values.about)}
          onChangeText={handleChange('about')}
          onBlur={handleBlur('about')}
          error={errors.about}
          touched={touched.about}
          label="About"
          max={700}
          multiline
          preventLeadingSpace
          preventTrailingSpace
          placeholder="Write a short description about the hospital"
          required
          wrapperClassName="bg-white border"
        />
        <TextInput
          value={String(values.accreditation)}
          onChangeText={handleChange('accreditation')}
          onBlur={handleBlur('accreditation')}
          error={errors.accreditation}
          max={150}
          preventLeadingSpace
          preventTrailingSpace
          touched={touched.accreditation}
          label="Accredidation"
          placeholder="e.g., NABH, ISO 9001:2015"
          wrapperClassName="bg-white border"
        />
      </ProfileDetailSections>

      <ProfileDetailSections title="Specializations" icon="stethoscope2">
        <MultiSelectInput
          value={values.specializations}
          options={specializationsList}
          onChange={(value: any[]) =>
            setFieldValue(
              'specializations',
              value.map(item => item.id),
            )
          }
          onBlur={handleBlur('specializations')}
          error={errors.specializations as string}
          touched={touched.specializations as boolean}
          itemTextClassName="text-ui_blue !font-FigtreeMedium"
          placeholder="Select specializations"
          required
          wrapperClassName="bg-white border"
          lockedValues={lockedSpecializations}
        />
      </ProfileDetailSections>
      <Button
        isLoading={profileEditLoading}
        title="Save"
        onClick={() => handleSubmit()}
        disabled={!isValid || !dirty}
        className="w-full max-w-[160px] sm:max-w-[200px] self-center"
      />
    </>
  );
};
